import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode, useEffect } from "react";
import {
  DeepPartial,
  FieldValues,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { ZodTypeAny, output } from "zod";

type Props<ValidationSchema extends ZodTypeAny> = {
  schema?: ValidationSchema;
  defaultValues?: DeepPartial<Record<string, unknown>>;
  onSubmit: SubmitHandler<output<ValidationSchema>>;
  children: ReactNode;
  setMethods?: (method: UseFormReturn<FieldValues, any, undefined>) => void;
  mode?: "onSubmit" | "onBlur" | "onChange" | "onTouched" | "all";
  isSubmitting?: boolean; // Add submission state to prevent double submissions
};

export default function FormTemplate<ValidationSchema extends ZodTypeAny>({
  schema,
  defaultValues,
  onSubmit,
  children,
  setMethods,
  mode,
  isSubmitting = false,
}: Props<ValidationSchema>) {
  const methods = useForm({
    mode: mode ?? "all",
    defaultValues,
    ...(schema ? { resolver: zodResolver(schema) } : {}),
  });

  useEffect(() => {
    if (setMethods) setMethods(methods);
  }, [methods, setMethods]);

  // Create a wrapper function that prevents submission when already submitting
  const handleFormSubmit = (data: any) => {
    if (isSubmitting) {
      return;
    }
    onSubmit(data);
  };

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={methods.handleSubmit(handleFormSubmit)}>
        {children}
      </form>
    </FormProvider>
  );
}
